export interface UserIdentity {
  id: string;
  fullName: string;
}

export interface PathologistIdentity extends UserIdentity {
  credentials?: string | null;
  email?: string | null;
  facilityName?: string | null;
  isRemote?: boolean;
}

export interface AssignmentState {
  pathologist: PathologistIdentity;
  revision: number;
}

export interface CandidateLoad {
  currentDailyCount: number;
  projectedDailyCount: number;
  configuredMaximum?: number | null;
  exceedsConfiguredMaximum: boolean;
}

export type ReassignmentWarningCode =
  | "ACTIVE_REVIEW"
  | "CAPACITY_EXCEEDED"
  | "REMOTE_ROUTING_FOLLOW_UP"
  | "PHYSICAL_MATERIAL_FOLLOW_UP"
  | "INDEPENDENT_RELATED_ASSIGNMENT"
  | "ACTIVE_SEND_OUT";

export type ReassignmentWarningSeverity = "information" | "warning";

export interface ReassignmentWarning {
  code: ReassignmentWarningCode;
  severity: ReassignmentWarningSeverity;
  message: string;
  followUp?: string | null;
}

export interface ReplacementCandidate extends PathologistIdentity {
  load: CandidateLoad;
  warnings: ReassignmentWarning[];
}

export interface ActiveReview {
  active: boolean;
  acknowledgementRequired: boolean;
  reviewer?: PathologistIdentity | null;
  expiresAt?: string | null;
}

export type RelatedResponsibilityKind =
  | "additional_stain_child"
  | "qa_review"
  | "send_out"
  | "internal_discussion"
  | "correction"
  | "consultation"
  | "other";

export interface RelatedResponsibility {
  id?: string | null;
  kind: RelatedResponsibilityKind;
  label: string;
  assignee?: PathologistIdentity | null;
  followsParentAssignment: boolean;
  status?: string | null;
}

export interface ReassignmentContext {
  caseId: string;
  caseNumber: string;
  lifecycleStatus: string;
  isFinalized?: boolean;
  isPartiallyFinalized?: boolean;
  currentAssignment: AssignmentState;
  candidates: ReplacementCandidate[];
  activeReview: ActiveReview;
  relatedResponsibilities: RelatedResponsibility[];
  warnings: ReassignmentWarning[];
}

export interface AssignPathologistRequest {
  expectedAssignmentRevision: number;
  pathologistId: string;
}

export interface AssignPathologistResponse {
  caseId: string;
  caseNumber: string;
  currentAssignment: AssignmentState;
}

export interface ReplacePathologistRequest {
  requestId: string;
  expectedCurrentPathologistId: string;
  expectedAssignmentRevision: number;
  replacementPathologistId: string;
  reason: string;
  acknowledgeActiveReview: boolean;
}

export type AssignmentTransitionSource =
  | "manual_replacement"
  | "parent_inheritance";

export interface AssignmentHistoryEvent {
  id: string;
  caseId: string;
  transitionSource: AssignmentTransitionSource;
  rootEventId?: string | null;
  fromPathologist: PathologistIdentity;
  toPathologist: PathologistIdentity;
  changedBy: UserIdentity;
  reason: string;
  fromRevision: number;
  toRevision: number;
  lifecycleStatus: string;
  activeReviewer?: PathologistIdentity | null;
  activeReviewAcknowledged: boolean;
  occurredAt: string;
}

export interface DerivedChildTransition {
  caseId: string;
  caseNumber: string;
  eventId: string;
}

export interface ReplacePathologistResponse {
  replayed: boolean;
  caseId: string;
  caseNumber: string;
  previousAssignment: AssignmentState;
  currentAssignment: AssignmentState;
  event: AssignmentHistoryEvent;
  inheritedChildTransitions: DerivedChildTransition[];
  warnings: ReassignmentWarning[];
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AssignmentHistoryPage {
  data: AssignmentHistoryEvent[];
  pagination: Pagination;
}

export type AssignmentNoticeRecipientRole =
  | "incoming"
  | "outgoing"
  | "active_reviewer";

export interface AssignmentNotice {
  id: string;
  recipientRole: AssignmentNoticeRecipientRole;
  title: string;
  message: string;
  caseId: string;
  caseNumber: string;
  eventAssignment: AssignmentState;
  currentAssignment: AssignmentState;
  ownershipChangedAgain: boolean;
  reason: string;
  recordedWorkPreserved: boolean;
  unsavedDeviceWorkPreserved: false;
  followUps: ReassignmentWarning[];
  readAt?: string | null;
  createdAt: string;
}

export interface AssignmentNoticePage {
  data: AssignmentNotice[];
  unreadCount: number;
  serverTime: string;
  nextCursor: string;
  hasMore: boolean;
}

export interface ErrorResponse {
  code: string;
  message: string;
}

export interface StaleAssignmentConflict extends ErrorResponse {
  code: "PATHOLOGIST_ASSIGNMENT_STALE";
  currentAssignment: AssignmentState;
}

export interface IdempotencyKeyConflict extends ErrorResponse {
  code: "IDEMPOTENCY_KEY_REUSED";
}

export interface CurrentPathologistRequiredConflict extends ErrorResponse {
  code: "CURRENT_PATHOLOGIST_REQUIRED";
}

export interface SamePathologistError extends ErrorResponse {
  code: "SAME_PATHOLOGIST";
  currentAssignment: AssignmentState;
}

export interface IneligiblePathologistError extends ErrorResponse {
  code: "REPLACEMENT_PATHOLOGIST_INELIGIBLE";
}

export interface ActiveReviewAcknowledgementError extends ErrorResponse {
  code: "ACTIVE_REVIEW_ACKNOWLEDGEMENT_REQUIRED";
  activeReview: ActiveReview;
}

export type CasePathologistAssignmentError =
  | StaleAssignmentConflict
  | IdempotencyKeyConflict
  | CurrentPathologistRequiredConflict
  | SamePathologistError
  | IneligiblePathologistError
  | ActiveReviewAcknowledgementError;

export type AssignmentRecoveryStatus =
  | "idle"
  | "reconciling"
  | "retry_available"
  | "unresolved";

export interface AssignmentRecoveryState {
  status: AssignmentRecoveryStatus;
  canRetry: boolean;
  message?: string;
}

const assignmentErrorCodes = new Set([
  "PATHOLOGIST_ASSIGNMENT_STALE",
  "IDEMPOTENCY_KEY_REUSED",
  "CURRENT_PATHOLOGIST_REQUIRED",
  "SAME_PATHOLOGIST",
  "REPLACEMENT_PATHOLOGIST_INELIGIBLE",
  "ACTIVE_REVIEW_ACKNOWLEDGEMENT_REQUIRED",
]);

export function getCasePathologistAssignmentError(
  error: unknown
): CasePathologistAssignmentError | null {
  if (!error || typeof error !== "object") return null;

  const candidate = error as {
    code?: string;
    response?: { data?: { code?: string } };
  };
  const errorData = candidate.response?.data ?? candidate;
  return errorData.code && assignmentErrorCodes.has(errorData.code)
    ? (errorData as CasePathologistAssignmentError)
    : null;
}
